/* Composer (rules 8-13, spec 6.1; structure after assistant-ui ComposerPrimitive, MIT).
   Under the box: exactly "+", the microphone and Send (Stop while busy). The "+" menu attaches (file, photo or
   camera, Drive) or connects an app. One "Options" popover, opened from the text label above the box that states
   the current choice in words, holds model, effort, mode, web search, thinking, fast replies, temporary chat,
   the "Start with" links (compare, voice mode, templates, skills, assistants) and the cost line. */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  ArrowUp, Bot, ChevronDown, FileText, Globe, HardDrive, Image, Mic, Paperclip, Plug, Plus, Square,
  SquareTerminal, Telescope, X, type LucideIcon,
} from 'lucide-react';
import { Menu, type MenuGroupSpec } from './Menu';
import { Popover } from './Popover';
import { CapBadge } from './basics';
import { DEFAULT_MODELS, EFFORTS, type Effort } from './ModelPicker';
import { OptionalSlot, optionalComponent } from './optional';
import { cx } from './cx';

/* Feature components the Composer uses when those features are installed (lazy; each falls back). */
type DictationProps = { onText: (text: string, deleteBefore: number) => void; getContext?: () => string; language?: string; className?: string; label?: string };
type CostEstimateProps = { kind: string; params?: Record<string, unknown>; compact?: boolean };
const DictationButton = optionalComponent<DictationProps>(import.meta.glob('../features/voice/DictationButton.tsx'), 'DictationButton');
const CostEstimate = optionalComponent<CostEstimateProps>(import.meta.glob('../features/billing/estimate.tsx'), 'CostEstimate');

/** The billing estimate request for a costly mode (null for ordinary replies). */
export function estimateRequest(mode: ToolMode | null, model: string, effort: Effort): { kind: string; params: Record<string, unknown> } | null {
  if (mode === 'research') return { kind: 'research', params: { depth: 'deep' } };
  if (mode === 'agent') return { kind: 'agent', params: {} };
  if (mode === 'image') return { kind: 'image', params: { count: 1 } };
  if (mode === 'code') return { kind: 'sandbox', params: { minutes: 5 } };
  if (model === 'Heavy' || effort === 'Heavy') return { kind: 'chat', params: { model: 'Heavy', effort } };
  return null;
}

/** Deletes `deleteBefore` characters before `caret` and inserts `text` (dictation results). */
export function insertAt(value: string, caret: number, text: string, deleteBefore = 0): { value: string; caret: number } {
  const cut = Math.max(0, caret - deleteBefore);
  return { value: value.slice(0, cut) + text + value.slice(caret), caret: cut + text.length };
}

export type ToolMode = 'search' | 'research' | 'agent' | 'code' | 'image';
export const TOOL_MODES: { id: ToolMode; label: string; icon: LucideIcon; cap: string }[] = [
  { id: 'search', label: 'Web search', icon: Globe, cap: 'SEARCH-01' },
  { id: 'research', label: 'Deep research', icon: Telescope, cap: 'RESEARCH-01' },
  { id: 'agent', label: 'Agent', icon: Bot, cap: 'AGENT-01' },
  { id: 'code', label: 'Code', icon: SquareTerminal, cap: 'AGENT-03' },
  { id: 'image', label: 'Image', icon: Image, cap: 'MEDIA-01' },
];

/** Cost preview before expensive modes (rule 13). */
export function costEstimate(mode: ToolMode | null, model: string, effort: Effort): string {
  if (mode === 'research') return 'Deep research: about 40 to 90 credits';
  if (mode === 'agent') return 'Agent run: about 20 to 60 credits, approvals before any send or payment';
  if (mode === 'image') return 'Image: about 4 credits each';
  if (mode === 'code') return 'Code run: about 1 to 3 credits';
  if (model === 'Heavy' || effort === 'Heavy') return 'Heavy: about 6 to 10 credits per reply';
  return '';
}

export interface Attachment { id: string; name: string; file?: File; text?: string }
/** What the person pinned in Options when `routing="auto"`; every key left out stays automatic. */
export interface SendPins { mode?: ToolMode | 'chat'; model?: string; effort?: Effort; thinking?: boolean }
export interface SendContext {
  mode: ToolMode | null; model: string; effort: Effort; attachments: Attachment[]; thinking: boolean;
  /** Only with `routing="auto"`: the settings the person pinned (empty = everything automatic). */
  pinned?: SendPins;
}

export interface ComposerProps {
  onSend: (text: string, ctx: SendContext) => void;
  onStop?: () => void;
  busy?: boolean;
  placeholder?: string;
  /** Autosaves the draft locally per key (rule 11). */
  draftKey?: string;
  model?: string;
  onModelChange?: (m: string) => void;
  effort?: Effort;
  onEffortChange?: (e: Effort) => void;
  mode?: ToolMode | null;
  onModeChange?: (m: ToolMode | null) => void;
  skills?: { id: string; label: string }[];
  mentions?: { id: string; label: string; kind?: 'file' | 'person' | 'assistant' }[];
  /** Ignored: "Report" lives in the "⋯" menus of the chat and of each reply. Kept so older callers still compile. */
  onReport?: () => void;
  onDictate?: () => void;
  offline?: boolean;
  disabled?: boolean;
  /** Large mic (cover screen home). */
  big?: boolean;
  /** Extra content above the input (limit card, etc.). */
  notice?: ReactNode;
  /** Touch keyboards: Enter adds a new line, the button sends (rule 9). Defaults from the pointer type. */
  enterSends?: boolean;
  /** Ask the billing service for a live estimate before costly modes (default true; falls back to the static line). */
  liveEstimate?: boolean;
  /** Dictation language (BCP 47). */
  language?: string;
  /** Temporary chat checkbox in Options. */
  temporary?: boolean;
  onTemporaryChange?: (v: boolean) => void;
  /** "Start with" links in Options; a link shows only when its handler is given (skills and assistants always show). */
  onCompare?: () => void;
  onVoiceMode?: () => void;
  onTemplates?: () => void;
  onAllSkills?: () => void;
  /** "+" menu handlers. */
  onConnectApp?: () => void;
  onFromDrive?: () => void;
  /** Opens the full model settings (the chat's model panel) from a link in Options. */
  onModelOptions?: () => void;
  /** "auto": mode, model, effort and thinking are chosen for each message and Options pins them. The label reads
      "Auto" until something is pinned; model/effort/mode props and handlers are not used. */
  routing?: 'auto';
  /** Short context-usage words shown at the end of the Options label, e.g. "Context 42%". */
  contextHint?: string;
  /** Context meter and its controls, shown at the foot of Options. */
  contextPanel?: ReactNode | ((api: { close: () => void }) => ReactNode);
}

const AUTO = '__auto';
const EFFORT_WORD: Record<Effort, string> = { Fast: 'Fast', Standard: 'Balanced', Thinking: 'Thinking', Heavy: 'Heavy' };
const MODE_OPTIONS: { id: ToolMode | 'chat'; label: string; cap?: string }[] = [
  { id: 'chat', label: 'Chat' },
  { id: 'research', label: 'Deep research', cap: 'RESEARCH-01' },
  { id: 'agent', label: 'Agent', cap: 'AGENT-01' },
  { id: 'code', label: 'Code', cap: 'AGENT-03' },
  { id: 'image', label: 'Image', cap: 'MEDIA-01' },
];

const DEFAULT_SKILLS = [{ id: '/quiz', label: 'Make a quiz' }, { id: '/doi-check', label: 'Check references' }, { id: '/slides', label: 'Turn into slides' }];
const LARGE_PASTE = 2000;

const readDraft = (k?: string) => { if (!k) return ''; try { return localStorage.getItem(`cx.draft.${k}`) ?? ''; } catch { return ''; } };
const writeDraft = (k: string, v: string) => { try { if (v) localStorage.setItem(`cx.draft.${k}`, v); else localStorage.removeItem(`cx.draft.${k}`); } catch { /* storage blocked */ } };

let attSeq = 0;

export function Composer(props: ComposerProps) {
  const {
    onSend, onStop, busy = false, placeholder = 'Message Crosspoint AI +…', draftKey, skills = DEFAULT_SKILLS, mentions = [],
    onDictate, offline, disabled, big, notice,
  } = props;
  const [text, setText] = useState(() => readDraft(draftKey));
  const [modelS, setModelS] = useState('Auto');
  const [effortS, setEffortS] = useState<Effort>('Standard');
  const [modeS, setModeS] = useState<ToolMode | null>(null);
  const [thinking, setThinking] = useState(true);
  const [fast, setFast] = useState(false);
  const [webS, setWebS] = useState(false);
  const [tempS, setTempS] = useState(false);
  const [atts, setAtts] = useState<Attachment[]>([]);
  const [drag, setDrag] = useState(false);
  const [list, setList] = useState<'skills' | 'assistants' | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const photoRef = useRef<HTMLInputElement>(null);
  const estId = useId();

  const auto = props.routing === 'auto';
  const [pinMode, setPinMode] = useState<ToolMode | 'chat' | null>(null);
  const [pinModel, setPinModel] = useState<string | null>(null);
  const [pinEffort, setPinEffort] = useState<Effort | null>(null);
  const [pinThinking, setPinThinking] = useState<boolean | null>(null);
  const model = auto ? pinModel ?? 'Auto' : props.model ?? modelS;
  const effort = auto ? pinEffort ?? 'Standard' : props.effort ?? effortS;
  const mode = auto ? (pinMode === 'chat' ? null : pinMode) : props.mode !== undefined ? props.mode : modeS;
  const temporary = props.temporary ?? tempS;
  const web = webS || mode === 'search';
  const setModel = (m: string) => { if (auto) { setPinModel(m === AUTO ? null : m); return; } setModelS(m); props.onModelChange?.(m); };
  const setEffort = (e: Effort | typeof AUTO) => { if (auto) { setPinEffort(e === AUTO ? null : (e as Effort)); return; } setEffortS(e as Effort); props.onEffortChange?.(e as Effort); };
  const setMode = (m: ToolMode | null) => { setModeS(m); props.onModeChange?.(m); };
  const pickMode = (v: string) => {
    if (auto) { setPinMode(v === AUTO ? null : (v as ToolMode | 'chat')); if (v !== 'search') setWebS(false); return; }
    setMode(v === 'chat' ? null : (v as ToolMode));
  };
  const pins: SendPins = auto ? {
    ...(pinMode ? { mode: pinMode } : web ? { mode: 'search' as const } : {}),
    ...(pinModel ? { model: pinModel } : {}),
    ...(pinEffort ? { effort: pinEffort } : {}),
    ...(pinThinking !== null ? { thinking: pinThinking } : {}),
  } : {};
  const nothingPinned = auto && !Object.keys(pins).length;
  const resetPins = () => { setPinMode(null); setPinModel(null); setPinEffort(null); setPinThinking(null); setWebS(false); };
  const setWeb = (v: boolean) => { setWebS(v); if (!v && mode === 'search') setMode(null); };
  const setTemporary = (v: boolean) => { setTempS(v); props.onTemporaryChange?.(v); };
  /** The mode that goes out with the message: web search is its own checkbox. */
  const effMode: ToolMode | null = mode && mode !== 'search' ? mode : web ? 'search' : null;
  const est = costEstimate(effMode, model, effort);
  const estReq = props.liveEstimate === false ? null : estimateRequest(effMode, model, effort);
  const modeWord = mode && mode !== 'search' ? TOOL_MODES.find((t) => t.id === mode)?.label : undefined;
  const optionsLabel = auto
    ? [
      pinModel ?? undefined, pinEffort ? EFFORT_WORD[pinEffort] : undefined,
      pinMode === 'chat' ? 'Chat' : modeWord, web && !modeWord ? 'Web search' : undefined,
      pinThinking === null ? undefined : pinThinking ? 'Thinking on' : 'Thinking off', temporary ? 'Temporary' : undefined,
    ].filter(Boolean).join(' · ') || 'Auto'
    : [model, EFFORT_WORD[effort], modeWord, web && !modeWord ? 'Web search' : undefined, temporary ? 'Temporary' : undefined, props.contextHint].filter(Boolean).join(' · ');

  // Debounced draft autosave (300 ms).
  useEffect(() => {
    if (!draftKey) return;
    const t = setTimeout(() => writeDraft(draftKey, text), 300);
    return () => clearTimeout(t);
  }, [text, draftKey]);

  // Grow with content up to 40% of the viewport.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, window.innerHeight * 0.4)}px`;
  }, [text]);

  const enterSends = props.enterSends ?? (typeof document === 'undefined' || document.documentElement.dataset.pointer !== 'coarse');
  const canSend = !disabled && (text.trim().length > 0 || atts.length > 0);

  const send = () => {
    if (busy) { onStop?.(); return; }
    if (!canSend) return;
    onSend(text.trim(), { mode: effMode, model, effort, attachments: atts, thinking: pinThinking ?? thinking, ...(auto ? { pinned: pins } : {}) });
    setText('');
    setAtts([]);
    if (draftKey) writeDraft(draftKey, '');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape' && busy) { e.preventDefault(); onStop?.(); return; }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      if (enterSends || e.ctrlKey || e.metaKey) { e.preventDefault(); send(); }
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = e.clipboardData.getData('text');
    if (pasted.length > LARGE_PASTE) {
      e.preventDefault();
      setAtts((a) => [...a, { id: `att-${++attSeq}`, name: `Pasted text (${pasted.length.toLocaleString()} characters)`, text: pasted }]);
    }
  };

  const addFiles = (files: FileList | File[] | null) => {
    if (!files) return;
    setAtts((a) => [...a, ...Array.from(files).map((f) => ({ id: `att-${++attSeq}`, name: f.name, file: f }))]);
  };

  const insertToken = (t: string) => {
    setText((v) => (v && !v.endsWith(' ') ? `${v} ` : v) + t);
    taRef.current?.focus();
  };

  const plusGroups: MenuGroupSpec[] = [
    {
      id: 'add', label: 'Add',
      items: [
        { id: 'file', label: 'Attach a file', icon: Paperclip, cap: 'KNOW-01', onSelect: () => fileRef.current?.click() },
        { id: 'photo', label: 'Photo or camera', icon: Image, onSelect: () => photoRef.current?.click() },
        { id: 'drive', label: 'From Drive', icon: HardDrive, cap: 'KNOW-04', onSelect: props.onFromDrive },
        { id: 'app', label: 'Connect an app', icon: Plug, onSelect: props.onConnectApp },
      ],
    },
  ];

  const linkBtn = (key: string, label: string, onClick: () => void, extra?: { cap?: string; expanded?: boolean }) => (
    <button key={key} type="button" className="tl" onClick={onClick} data-cap={extra?.cap} aria-expanded={extra?.expanded}>{label}</button>
  );

  const modelDesc = DEFAULT_MODELS.flatMap((g) => g.models).find((m) => m.id === model)?.description;

  const optionsPanel = ({ close }: { close: () => void }) => (
    <div className="opts">
      {auto && (
        <p className="opts-auto" data-testid="opts-auto">
          <b>{nothingPinned ? 'Automatic (recommended)' : 'Some choices are pinned'}</b>
          <span className="muted"> Ask picks the mode, model, effort and thinking for each message and balances cost; it asks before anything costly. Pin any of them below.</span>
          {!nothingPinned && <> <button type="button" className="tl" onClick={resetPins}>Back to automatic</button></>}
        </p>
      )}
      <div className="opts-grid">
        <label data-cap="MODEL-02"><span>Model</span>
          <select value={auto ? pinModel ?? AUTO : model} onChange={(e) => setModel(e.target.value)}>
            {auto && <option value={AUTO}>Automatic (recommended)</option>}
            {!DEFAULT_MODELS.some((g) => g.models.some((m) => m.id === model)) && !(auto && !pinModel) && <option value={model}>{model}</option>}
            {DEFAULT_MODELS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.models.filter((m) => !(auto && m.id === 'Auto')).map((m) => <option key={m.id} value={m.id}>{m.label ?? m.id}</option>)}
              </optgroup>
            ))}
          </select>
        </label>
        <label data-cap="MODEL-04"><span>Effort</span>
          <select value={auto ? pinEffort ?? AUTO : effort} onChange={(e) => setEffort(e.target.value as Effort)}>
            {auto && <option value={AUTO}>Automatic (recommended)</option>}
            {EFFORTS.map((e) => <option key={e.value} value={e.value}>{EFFORT_WORD[e.value]}</option>)}
          </select>
        </label>
      </div>
      {!(auto && !pinModel && !pinEffort) && <span className="opts-desc muted">{modelDesc ? `${model}: ${modelDesc}. ` : ''}{EFFORT_WORD[effort]} effort: {EFFORTS.find((e) => e.value === effort)?.hint} per reply.</span>}
      <label><span>Mode</span>
        <select aria-label="Mode" value={auto ? pinMode ?? AUTO : mode && mode !== 'search' ? mode : 'chat'} onChange={(e) => pickMode(e.target.value)}>
          {auto && <option value={AUTO}>Automatic (recommended)</option>}
          {MODE_OPTIONS.map((m) => <option key={m.id} value={m.id} data-cap={m.cap}>{auto && m.id === 'chat' ? 'Chat only' : m.label}</option>)}
        </select>
      </label>
      <div className="opts-checks">
        <label className="chk" data-cap="SEARCH-01"><input type="checkbox" checked={web} onChange={(e) => setWeb(e.target.checked)} /> Web search</label>
        {auto ? (
          <label><span>Thinking</span>
            <select aria-label="Thinking" value={pinThinking === null ? AUTO : pinThinking ? 'on' : 'off'} onChange={(e) => setPinThinking(e.target.value === AUTO ? null : e.target.value === 'on')}>
              <option value={AUTO}>Automatic (recommended)</option>
              <option value="on">Always think</option>
              <option value="off">Never think</option>
            </select>
          </label>
        ) : (
          <label className="chk"><input type="checkbox" checked={thinking} onChange={(e) => setThinking(e.target.checked)} /> Thinking</label>
        )}
        <label className="chk"><input type="checkbox" checked={fast} onChange={(e) => setFast(e.target.checked)} /> Fast replies</label>
        <label className="chk"><input type="checkbox" checked={temporary} onChange={(e) => setTemporary(e.target.checked)} /> Temporary chat</label>
      </div>
      <div className="opts-start">
        <span className="opts-h">Start with</span>
        <div className="opts-links">
          {props.onCompare && linkBtn('compare', 'Compare models', () => { close(); props.onCompare!(); }, { cap: 'MODEL-05' })}
          {props.onVoiceMode && linkBtn('voice', 'Voice mode', () => { close(); props.onVoiceMode!(); })}
          {props.onTemplates && linkBtn('templates', 'Templates', () => { close(); props.onTemplates!(); })}
          {linkBtn('skills', 'Skills /', () => setList(list === 'skills' ? null : 'skills'), { expanded: list === 'skills' })}
          {linkBtn('assistants', 'Assistants @', () => setList(list === 'assistants' ? null : 'assistants'), { expanded: list === 'assistants' })}
        </div>
        {list === 'skills' && (
          <div className="opts-links" role="group" aria-label="Skills">
            {skills.map((s) => linkBtn(s.id, `${s.id} ${s.label}`, () => { close(); insertToken(`${s.id} `); }))}
            {props.onAllSkills && linkBtn('all-skills', 'All skills', () => { close(); props.onAllSkills!(); }, { cap: 'ASSIST-02' })}
          </div>
        )}
        {list === 'assistants' && (
          <div className="opts-links" role="group" aria-label="Assistants and mentions">
            {mentions.length
              ? mentions.map((m) => linkBtn(m.id, `@ ${m.label}`, () => { close(); insertToken(`@${m.label.split(' ')[0]} `); }))
              : linkBtn('at', 'Type @ in the box', () => { close(); insertToken('@'); })}
          </div>
        )}
      </div>
      {props.onModelOptions && (
        <div className="opts-links">{linkBtn('model-options', 'More model options', () => { close(); props.onModelOptions!(); }, { cap: 'MODEL-04' })}</div>
      )}
      {props.contextPanel && <div className="opts-ctx">{typeof props.contextPanel === 'function' ? props.contextPanel({ close }) : props.contextPanel}</div>}
      <div className="opts-cost" id={estId}>
        {est && estReq
          ? <span className="est-live"><OptionalSlot C={CostEstimate} fallback={<span>{est}</span>} props={{ kind: estReq.kind, params: estReq.params, compact: true }} /></span>
          : <span>{est || 'A normal reply has no extra estimate.'}</span>}
        <span className="muted"> Estimated before you send.</span>
      </div>
    </div>
  );

  return (
    <div className="composer-wrap" data-cap="CHAT-02">
      {notice}
      <div
        className={cx('composer', drag && 'drag')}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
      >
        <CapBadge id="CHAT-02" />
        <div className="ctop">
          <Popover
            label="Options"
            align="end"
            prefer="above"
            trigger={(p) => (
              <button type="button" className="optlabel" {...p} ref={p.ref} aria-label={`Options: ${optionsLabel}`}>
                <span>{optionsLabel}</span><ChevronDown size={16} aria-hidden="true" />
              </button>
            )}
          >
            {optionsPanel}
          </Popover>
        </div>
        {atts.length > 0 && (
          <div className="att-tray">
            {atts.map((a) => (
              <span className="att" key={a.id}>
                <FileText size={14} aria-hidden="true" />{a.name}
                <button type="button" className="icon-btn sm" aria-label={`Remove ${a.name}`} onClick={() => setAtts((l) => l.filter((x) => x.id !== a.id))}><X size={14} aria-hidden="true" /></button>
              </span>
            ))}
          </div>
        )}
        <textarea
          ref={taRef}
          rows={1}
          aria-label="Message"
          placeholder={drag ? 'Drop to attach' : placeholder}
          value={text}
          disabled={disabled}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
        <input ref={fileRef} type="file" multiple hidden aria-hidden="true" tabIndex={-1} onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        <input ref={photoRef} type="file" accept="image/*" multiple hidden aria-hidden="true" tabIndex={-1} onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        <div className="ctools">
          <Menu label="Add a file, photo, Drive item or app" triggerIcon={Plus} asSheet="auto" align="start" groups={plusGroups} />
          <span className="sp" />
          {(() => {
            const plainMic = (
              <button type="button" className={big ? 'send' : 'icon-btn'} style={big ? { width: 56, height: 56 } : undefined} aria-label="Dictate" onClick={onDictate} data-cap="VOICE-02">
                <Mic size={18} aria-hidden="true" />
              </button>
            );
            // A feature's own handler wins; otherwise the voice feature's DictationButton types into this box.
            if (onDictate || big || disabled) return plainMic;
            return (
              <OptionalSlot
                C={DictationButton}
                fallback={plainMic}
                props={{
                  label: 'Dictate',
                  language: props.language,
                  getContext: () => { const ta = taRef.current; return ta ? ta.value.slice(0, ta.selectionStart ?? ta.value.length) : text; },
                  onText: (t, del) => {
                    const ta = taRef.current;
                    const caret = ta?.selectionStart ?? text.length;
                    setText((v) => insertAt(v, Math.min(caret, v.length), t, del).value);
                  },
                }}
              />
            );
          })()}
          <button
            type="button"
            className={cx('send', busy && 'stop')}
            aria-label={busy ? 'Stop generating (Esc)' : offline ? 'Send when online' : 'Send (Enter)'}
            disabled={!busy && !canSend}
            onClick={send}
          >
            {busy ? <Square size={14} fill="currentColor" aria-hidden="true" /> : <ArrowUp size={18} aria-hidden="true" />}
          </button>
        </div>
        {offline && <div className="est">Will send when online</div>}
      </div>
      {est && <div className="est" role="status">{est}. Estimated before you send.</div>}
      <div className="disc" data-cap="SAFE-01">AI replies can be wrong, so check what matters.</div>
    </div>
  );
}
